import { Form } from 'antd';
import { forwardRef, type TextareaHTMLAttributes } from 'react';

import Input from '@/components/Inputs/Input';

const DEFINITION_MAX_LENGTH = 500;

const rules = {
  keyword: {
    required: { required: true, message: 'Keyword is required' },
  },
  definition: {
    maxLength: {
      max: DEFINITION_MAX_LENGTH,
      message: `Definition must be ${DEFINITION_MAX_LENGTH} characters or less`,
    },
  },
} as const;

type KeywordFieldProps = {
  required?: boolean;
  showTooltip?: boolean;
};

export const KeywordField = ({
  required = false,
  showTooltip = true,
}: KeywordFieldProps) => (
  <Form.Item
    label="Keyword"
    name="keyword"
    required={required}
    rules={required ? [rules.keyword.required] : []}
    tooltip={
      showTooltip
        ? 'The term to scrape. e.g., brand name, product, topic.'
        : undefined
    }
  >
    <Input placeholder="e.g., Apple" sizeType="m" />
  </Form.Item>
);

type DefinitionFieldProps = {
  required?: boolean;
};
export const DefinitionField = ({ required = false }: DefinitionFieldProps) => (
  <Form.Item
    label="Definition"
    name="definition"
    required={required}
    rules={[rules.definition.maxLength]}
    tooltip="Describe what this term means in your specific context."
  >
    <Textarea
      className="w-full h-25 p-2 border border-gray-300 rounded-md text-sm outline-none resize-none"
      placeholder="e.g., 'Apple' as the tech company, not the fruit."
      maxLength={DEFINITION_MAX_LENGTH}
    />
  </Form.Item>
);

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ maxLength, value, ...props }, ref) => (
    <div className="relative">
      <textarea ref={ref} value={value} maxLength={maxLength} {...props} />
      {maxLength && (
        <span className="absolute bottom-2 right-2 text-xs text-gray-400">
          {String(value || '').length}/{maxLength}
        </span>
      )}
    </div>
  ),
);
